import { Component, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommissionTypeOptions, ICartItem, InvoicingService, ItemType } from '../../core/services/invoicing.service';
import { MerchantsService } from '../../core/services/merchants.service';
import { ToolsService } from '../../core/services/tools.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslatePipe } from '@ngx-translate/core';
import { TextInputComponent } from '../../shared-with-client/text-input/text-input.component';
import { TabBarComponent } from '../../shared/tab-bar/tab-bar.component';
import { DropdownInputV2Component } from '../../shared-with-client/dropdown-input-v2/dropdown-input-v2.component';
import { CheckboxInputComponent } from '../../shared-with-client/checkbox-input/checkbox-input.component';

@Component({
    selector: 'app-invoice-item-edit-modal',
    imports: [TranslatePipe, TextInputComponent, ReactiveFormsModule, TabBarComponent, DropdownInputV2Component, CheckboxInputComponent],
    templateUrl: './invoice-item-edit-modal.component.html',
    styleUrl: './invoice-item-edit-modal.component.scss',
})
export class InvoiceItemEditModalComponent {
    private invoicingService = inject(InvoicingService);
    private merchantService = inject(MerchantsService);
    private toolsService = inject(ToolsService);
    public currentMerchant = toSignal(this.merchantService.activeMerchant);

    public item!: ICartItem;

    public totalInvoicingCartTotals = toSignal(this.invoicingService.invoicingCartTotals$);
    public availableProvidersForCommissions = toSignal(this.invoicingService.$availableProvidersForCommissions, { initialValue: [] });

    public vatExemptions = this.invoicingService.getVatExemptionOptions(this.currentMerchant()?.country);

    public bsModalRef = inject(BsModalRef);

    submitAttempted = false;
    saving = signal(false);

    formBuilder = inject(FormBuilder);
    form!: FormGroup;

    activeTab = 'discountAmountCents';
    tabOptions = [
        {
            label: (this.toolsService.formatAsCurrency(0, this.currentMerchant()?.currency)?.replace('0', '') || '€')?.trim(),
            value: 'discountAmountCents',
        },
        { label: '%', value: 'discountPercentage' },
    ];

    vatRatesOptions = this.invoicingService.getVatRateOptions(this.currentMerchant()?.country);

    public merchantCurrency = (this.toolsService.formatAsCurrency(0, this.currentMerchant()?.currency)?.replace('0', '') || '€')?.trim();

    CommissionTypeOptions = CommissionTypeOptions;

    ngOnInit(): void {
        this.generateForm(this.item);

        this.activeTab = this.item.discountPercentage && this.item.discountPercentage > 0 ? 'discountPercentage' : 'discountAmountCents';
    }

    get providersArray() {
        return this.form.get('providers') as FormArray;
    }

    generateForm(item: ICartItem) {
        const maxDiscount = (item.unitPriceCents || 0) / 100;

        const vatRate = this.vatRatesOptions.find((v) => v.value === (item?.vatRate ?? this.invoicingService.getDefaultVatRate(this.currentMerchant()?.country))) || null;
        const vatExemptReason = this.vatExemptions.find((v) => v.value === item?.vatExemptReason) || null;

        this.form = this.formBuilder.group({
            quantity: [item.quantity || 1, [Validators.required, Validators.min(1)]],
            discountPercentage: [item.discountPercentage || 0, [Validators.min(0), Validators.max(100)]],
            discountAmountCents: [(item.discountAmountCents || 0) / 100, [Validators.min(0), Validators.max(maxDiscount)]],
            vatRate: [vatRate ? vatRate : null, this.vatRatesOptions.length ? [Validators.required, Validators.min(0)] : []],
            unitPriceCents: [(item.unitPriceCents || 0) / 100, [Validators.required, Validators.min(0)]],
            name: [item.itemName, [Validators.required]],
            vatExemptReason: [vatExemptReason || null],
            hasProviders: [item.providers && item.providers.length > 0],
        });

        if (item.providers?.length && (item.itemType === 'service' || item.itemType === 'product')) {
            const providersFormArray: FormArray = this.formBuilder.array([]);
            item.providers.forEach((provider) => {
                const providerInList = this.availableProvidersForCommissions().find((p: any) => p.id === provider.providerId);
                const group = this.formBuilder.group({
                    provider: [providerInList || null, Validators.required],
                    commissionType: [provider.commissionType, Validators.required],
                    commissionValue: [provider.commissionValue, [Validators.min(0)]],
                });
                this.setupProviderListener(group);
                providersFormArray.push(group);
            });
            this.form.addControl('providers', providersFormArray);
        }

        this.setupVatExemption(this.form.get('vatRate')?.value?.value);

        this.form.get('vatRate')?.valueChanges.subscribe((value) => {
            this.setupVatExemption(value?.value);
        });

        this.form.get('unitPriceCents')?.valueChanges.subscribe((value) => {
            const maxDiscount = this.form.get('unitPriceCents')?.value || 0;
            this.form.get('discountAmountCents')?.setValidators([Validators.min(0), Validators.max(maxDiscount)]);
            this.form.get('discountAmountCents')?.updateValueAndValidity();
        });

        this.form.get('hasProviders')?.valueChanges.subscribe((hasProviders) => {
            if (hasProviders && !this.form.get('providers')) {
                this.form.addControl('providers', this.formBuilder.array([]));
                this.addAssociatedProvider();
            } else if (!hasProviders && this.form.get('providers')) {
                this.form.removeControl('providers');
            }
        });
    }

    addAssociatedProvider() {
        const providersFormArray = this.form.get('providers') as FormArray;
        const group = this.formBuilder.group({
            provider: [null, Validators.required],
            commissionType: [CommissionTypeOptions.PERCENTAGE, Validators.required],
            commissionValue: [0, [Validators.min(0)]],
        });
        this.setupProviderListener(group);
        providersFormArray.push(group);
    }

    setupProviderListener(group: FormGroup) {
        group.get('provider')?.valueChanges.subscribe((provider) => {
            if (!provider) return;
            if (provider && (this.item.serviceId || this.item.productId)) {
                const itemType = this.item.serviceId ? ItemType.SERVICE : ItemType.PRODUCT;
                const defaultCommission = this.invoicingService.getDefaultCommissionRate(provider, this.item.serviceId ?? this.item.productId, itemType);
                group.patchValue({
                    commissionType: defaultCommission.commissionType || CommissionTypeOptions.PERCENTAGE,
                    commissionValue: defaultCommission.commissionValue || 0,
                });
            }
        });
    }

    removeAssociatedProvider(index: number) {
        const providersFormArray = this.form.get('providers') as FormArray;
        providersFormArray.removeAt(index);
        if (providersFormArray.length === 0) {
            this.form.get('hasProviders')?.setValue(false);
        }
    }

    setupVatExemption(vatRate: number) {
        if (vatRate === 0 && this.vatExemptions.length) {
            this.form.get('vatExemptReason')?.setValidators([Validators.required]);
        } else {
            this.form.get('vatExemptReason')?.clearValidators();
            this.form.get('vatExemptReason')?.setValue(null);
        }
        this.form.get('vatExemptReason')?.updateValueAndValidity();
    }

    onTabChanged(tab: string) {
        if (tab === 'discountAmountCents') {
            this.form.get('discountAmountCents')?.enable();
            this.form.get('discountPercentage')?.disable();
        } else if (tab === 'discountPercentage') {
            this.form.get('discountPercentage')?.enable();
            this.form.get('discountAmountCents')?.disable();
        }
    }

    save() {
        this.submitAttempted = true;
        if (this.form.invalid) {
            return;
        }
        this.saving.set(true);
        const formValue = this.form.value;
        this.invoicingService.updateCartItem(this.item.cartId ?? crypto.randomUUID(), {
            quantity: Number(formValue.quantity),
            discountPercentage: this.activeTab === 'discountPercentage' ? formValue.discountPercentage || 0 : 0,
            discountAmountCents: this.activeTab === 'discountAmountCents' ? Math.round(formValue.discountAmountCents * 100 || 0) : 0,
            vatRate: formValue.vatRate?.value,
            unitPriceCents: Math.round(formValue.unitPriceCents * 100),
            vatExemptReason: formValue.vatExemptReason?.value || null,
            providers: formValue.providers?.length
                ? formValue.providers.map((v: any) => {
                      return {
                          ...v,
                          providerId: Number(v.provider?.id),
                          commissionValue: Number(v.commissionValue),
                          providerName: this.availableProvidersForCommissions().find((p: any) => p.id === v.provider.id)?.name || '',
                      };
                  })
                : [],
        });

        this.saving.set(false);
        this.close();
    }

    close() {
        this.bsModalRef.hide();
    }
}
